import { GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';
import { useGradeConversionPageProps } from '~/data/useGradeConversionPageProps';
import { useGradePointMappings } from '../../GradeConversionDetails/data/useGradePointMappings';
import { useGradeSchemas } from '~/data/useGradeSchemas';
import {
  CountryOption,
  EducationLevelOption,
  mapCountries,
  mapEducationLevels,
} from '../../GradeConversion/utils/referenceData';

type LoadingResult = {
  isLoading: true;
  error?: undefined;
  gradeSchemas?: undefined;
  gradePointMappings?: undefined;
  educationLevels?: undefined;
  countries?: undefined;
};

type ErrorResult = {
  isLoading: false;
  error: Error;
  gradeSchemas?: undefined;
  gradePointMappings?: undefined;
  educationLevels?: undefined;
  countries?: undefined;
};

type ReadyResult = {
  isLoading: false;
  error?: undefined;
  gradeSchemas: GradeSchemaInfo[];
  gradePointMappings: GradePointMapping[];
  educationLevels: EducationLevelOption[];
  countries: CountryOption[];
};

export type UseGradeConversionCreatePageDataResult = LoadingResult | ErrorResult | ReadyResult;

export function useGradeConversionCreatePageData(): UseGradeConversionCreatePageDataResult {
  const { initialProps, isLoadingInitialProps, isInitialPropsError, initialPropsError } = useGradeConversionPageProps();
  const { gradePointMappings, isLoadingGradePointMappings, isGradePointMappingsError, gradePointMappingsError } =
    useGradePointMappings();
  const { gradeSchemas, isLoadingGradeSchemas, isGradeSchemasError, gradeSchemasError } = useGradeSchemas();

  if (isLoadingInitialProps || isLoadingGradePointMappings || isLoadingGradeSchemas) {
    return { isLoading: true };
  }

  if (isInitialPropsError || !initialProps) {
    return {
      isLoading: false,
      error: (initialPropsError as Error) ?? new Error('Failed to load grade conversion reference data.'),
    };
  }

  if (isGradePointMappingsError || gradePointMappings.length === 0) {
    return {
      isLoading: false,
      error: (gradePointMappingsError as Error) ?? new Error('Grade point mappings not found.'),
    };
  }

  if (isGradeSchemasError || gradeSchemas.length === 0) {
    return {
      isLoading: false,
      error: (gradeSchemasError as Error) ?? new Error('Grade schemas not found.'),
    };
  }

  const countries = mapCountries(initialProps.referenceData.countries);
  const educationLevels = mapEducationLevels(initialProps.referenceData.educationLevels);

  return {
    isLoading: false,
    gradeSchemas,
    gradePointMappings,
    educationLevels,
    countries,
  };
}
